import React, { useEffect, useMemo, useState } from "react";
import { FaPencilAlt, FaRegClipboard } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Sidebar from "../../../../../../Sidebar.js";
import Header from "../../../../../../header/header.js";
import Navvbar from "../../../../../../header/Navvbar.js";
import Footer from "../../../../../../footer.js";
import "../../../CaseSetting.css";
import AddTask from "./AddTask";
import EditTask from "./EditTask";
import CaseTasks from "./CaseTasks";
import ProgressStages from "./ProgressStages";
import ScanningAssigning from "./ScanningAssigning";
import UndoCompletedTasks from "./UndoCompletedTasks";
import "./Tasks.css";

// Replace with your API data (e.g. useEffect + axios) when ready
const INITIAL_TASKS = [
  { id: 1, name: "BL Tracking", department: "", turnAroundDays: "", dailyLimit: "" },
  { id: 2, name: "Cases To Outsource", department: "", turnAroundDays: "", dailyLimit: "" },
  { id: 3, name: "Create Printing File", department: "CROWN AND BRIDGE", turnAroundDays: "", dailyLimit: "" },
  { id: 4, name: "Date Management", department: "All Departments", turnAroundDays: 0, dailyLimit: "" },
  { id: 5, name: "Delivery Monitoring", department: "", turnAroundDays: "", dailyLimit: "" },
  { id: 6, name: "Designing", department: "CROWN AND BRIDGE", turnAroundDays: 0, dailyLimit: "" },
  { id: 7, name: "Sent to STSB", department: "CROWN AND BRIDGE", turnAroundDays: "", dailyLimit: "" },
  { id: 8, name: "UK team Coordination", department: "", turnAroundDays: 0, dailyLimit: "" },
];

// Keeps the list when moving between the different page URLs (component remounts)
let tasksStore = INITIAL_TASKS;

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "department", label: "Department" },
  { key: "turnAroundDays", label: "Turn Around Days" },
  { key: "dailyLimit", label: "Daily Limit" },
];

// URLs of every page (all routes are defined in App.js)
const LIST_URL = "/caseSetting/tasks";
const ADD_URL = "/add-task";
const EDIT_URL = "/edit-task";

const ADD_MENU = [
  { label: "Case Tasks", icon: <FaPencilAlt />, path: "/case-tasks" },
  { label: "Progress Stages", icon: <FaPencilAlt />, path: "/progress-stages" },
  { label: "Scanning & Assigning", icon: <FaRegClipboard />, path: "/task-scanning-assigning" },
  { label: "Undo Completed Tasks", icon: <FaRegClipboard />, path: "/undo-completed-tasks" },
];

const PAGE_SIZE = 10;

const slugify = (t) =>
  String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const PROCEDURE_TABS = [
  { key: "Lab Procedures", disabled: false },
  { key: "Display Categories", disabled: false },
  { key: "Tasks", disabled: false },
  { key: "Departments", disabled: false },
  { key: "Components", disabled: true },
  { key: "Enclosures", disabled: true },
  { key: "Specified Files", disabled: true },
  { key: "Revenue Centers", disabled: true },
].map((t) => ({ ...t, slug: slugify(t.key) }));

const Tasks = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tasks, setTasksState] = useState(tasksStore);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);

  const setTasks = (updater) => {
    setTasksState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      tasksStore = next;
      return next;
    });
  };

  // ---------- URL-based pages ----------
  const path = location.pathname.replace(/\/+$/, "");
  // Edit task id travels in navigation state (not in the URL), it survives refresh
  const editId = location.state?.taskId;

  let form = null;
  let view = null;
  let editNotFound = false;

  if (path === ADD_URL) {
    form = { task: null };
  } else if (path === EDIT_URL) {
    const found = tasks.find((t) => String(t.id) === String(editId));
    form = found ? { task: found } : null;
    editNotFound = !found;
  } else if (path === "/case-tasks") {
    view = "caseTasks";
  } else if (path === "/progress-stages") {
    view = "progressStages";
  } else if (path === "/task-scanning-assigning") {
    view = "scanning";
  } else if (path === "/undo-completed-tasks") {
    view = "undo";
  }

  const goToList = () => navigate(LIST_URL);
  const goToAdd = () => navigate(ADD_URL);
  const goToEdit = (task) => navigate(EDIT_URL, { state: { taskId: task.id } });
  const goToSub = (p) => navigate(p);

  // /edit-task opened without a valid task (e.g. typed manually) -> back to the list
  useEffect(() => {
    if (editNotFound) navigate(LIST_URL, { replace: true });
  }, [editNotFound, navigate]);

  // close any open dropdown when clicking elsewhere
  useEffect(() => {
    const close = () => setOpenMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? tasks.filter((t) => COLUMNS.some((c) => String(t[c.key] ?? "").toLowerCase().includes(q)))
      : [...tasks];
    if (sort.key) {
      list.sort((a, b) => {
        const r = String(a[sort.key] ?? "").localeCompare(String(b[sort.key] ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
        return sort.dir === "asc" ? r : -r;
      });
    }
    return list;
  }, [tasks, search, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const start = (current - 1) * PAGE_SIZE;
  const rows = filtered.slice(start, start + PAGE_SIZE);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));
  const arrowClass = (key) =>
    "tk-sort" + (sort.key === key ? (sort.dir === "asc" ? " tk-asc" : " tk-desc") : "");
  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));
  const toggleMenu = (e, id) => {
    e.stopPropagation();
    setOpenMenu(openMenu === id ? null : id);
  };

  const handleSave = (fields) => {
    if (form?.task) {
      setTasks((list) => list.map((x) => (x.id === form.task.id ? { ...x, ...fields } : x)));
    } else {
      setTasks((list) => [...list, { id: Date.now(), ...fields }]);
    }
    goToList();
  };

  const departments = [
    ...new Set(["All Departments", "CROWN AND BRIDGE", ...tasks.map((x) => x.department).filter(Boolean)]),
  ];

  const content = (
    <div className="tk-page">
      <h3 className="tk-title">Tasks</h3>

      <div className="tk-outer">
        <div className="tk-inner">
          <div className="tk-toolbar">
            <div className="tk-search">
              <label htmlFor="tk-search">Search</label>
              <input
                id="tk-search"
                type="text"
                placeholder="Type to search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="tk-split">
              <button type="button" className="tk-btn tk-add" onClick={goToAdd}>
                <span className="tk-plus">+</span> Add
              </button>
              <button type="button" className="tk-btn tk-caret" onClick={(e) => toggleMenu(e, "add")}>
                ▼
              </button>
              {openMenu === "add" && (
                <div className="tk-menu tk-menu-lg">
                  {ADD_MENU.map((m) => (
                    <button type="button" key={m.label} onClick={() => goToSub(m.path)}>
                      {m.icon}
                      <span>{m.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="tk-table-wrap">
            <table className="tk-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}>
                      <span>{c.label}</span>
                      <i className={arrowClass(c.key)} />
                    </th>
                  ))}
                  <th className="tk-actions-col" />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id}>
                    <td>{t.name}</td>
                    <td>{t.department}</td>
                    <td>{t.turnAroundDays}</td>
                    <td>{t.dailyLimit}</td>
                    <td className="tk-actions-col">
                      <div className="tk-split tk-small">
                        <button type="button" className="tk-btn tk-add" onClick={() => goToEdit(t)}>
                          Edit
                        </button>
                        <button type="button" className="tk-btn tk-caret" onClick={(e) => toggleMenu(e, t.id)}>
                          ▼
                        </button>
                        {openMenu === t.id && (
                          <div className="tk-menu">
                            <button type="button" onClick={() => goToEdit(t)}>
                              <FaPencilAlt />
                              <span>Edit Task</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={5} className="tk-empty">No tasks found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="tk-footer">
            <span>
              Showing {filtered.length ? start + 1 : 0} to {start + rows.length} of {filtered.length} entries
            </span>
            <div className="tk-pager">
              <button type="button" onClick={() => goTo(1)} disabled={current === 1}>First</button>
              <button type="button" onClick={() => goTo(current - 1)} disabled={current === 1}>Previous</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button key={p} type="button" className={p === current ? "tk-active" : ""} onClick={() => goTo(p)}>
                  {p}
                </button>
              ))}
              <button type="button" onClick={() => goTo(current + 1)} disabled={current === totalPages}>Next</button>
              <button type="button" onClick={() => goTo(totalPages)} disabled={current === totalPages}>Last</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="cs-header-container">
        <Header />
        <Navvbar />
      </div>
      <div className="cs-main-wrapper">
        <div className="row cs-row">
          <div className="col-md-2 cs-sidebar">
            <Sidebar />
          </div>
          <div className="col-md-10 cs-content">
            {form ? (
              form.task ? (
                <EditTask
                  key={form.task.id}
                  task={form.task}
                  departments={departments}
                  onSave={handleSave}
                  onCancel={goToList}
                />
              ) : (
                <AddTask
                  departments={departments}
                  onSave={handleSave}
                  onCancel={goToList}
                />
              )
            ) : (
              <>
                <h1 className="cs-content-title">Case Settings</h1>
                <div className="cs-tabs-title">
                  <button className="cs-tab-btn" onClick={() => navigate("/caseSetting")}>General</button>
                  <span className="cs-tab-gap"></span>
                  <button className="cs-tab-btn cs-tab-active">Procedures</button>
                  <span className="cs-tab-gap"></span>
                  <button className="cs-tab-btn cs-tab-disabled" disabled title="Attributes tab is currently disabled">Attributes</button>
                </div>

                <div className="cs-tabs-container">
                  <Tabs
                    activeKey="Tasks"
                    onSelect={(k) => {
                      const tab = PROCEDURE_TABS.find((t) => t.key === k);
                      if (!tab || tab.disabled) return;
                      if (tab.key === "Tasks") goToList();
                      else navigate(`/caseSetting/${tab.slug}`);
                    }}
                    id="procedures-tabs"
                    className="mb-3"
                  >
                    {PROCEDURE_TABS.map((tab) => (
                      <Tab key={tab.key} eventKey={tab.key} title={tab.key} disabled={tab.disabled} />
                    ))}
                  </Tabs>

                  <div className="tab-content-wrapper">
                    {view === "caseTasks" ? (
                      <CaseTasks tasks={tasks} onSave={goToList} />
                    ) : view === "progressStages" ? (
                      <ProgressStages />
                    ) : view === "scanning" ? (
                      <ScanningAssigning onSave={goToList} />
                    ) : view === "undo" ? (
                      <UndoCompletedTasks onSave={goToList} />
                    ) : (
                      content
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="cs-footer-wrapper">
        <div className="cs-footer-inner"><Footer /></div>
      </div>
    </>
  );
};

export default Tasks;